import {
  HoveredLink,
  Menu,
  MenuItem,
} from "@/components/ui/aceternity-ui/navbar-menu";
import { ArtifactLogo } from "@/components/ui/artifact-logo";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/contexts/ThemeProvider";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store";
import { useMotionValueEvent, useScroll } from "framer-motion";
import { Laptop, Menu as MenuIcon, Moon, Sun, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

// Optimized CSS animations to replace heavy Framer Motion
const topbarAnimations = `
  @keyframes slideInFromTop {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes scaleHover {
    from { transform: scale(1); }
    to { transform: scale(1.03); }
  }
  
  @keyframes scalePress {
    from { transform: scale(1); }
    to { transform: scale(0.97); }
  }
  
  .topbar-slide-in {
    animation: slideInFromTop 0.3s ease-out;
  }
  
  .topbar-fade-in {
    animation: fadeIn 0.3s ease-out;
  }
  
  .button-hover:hover {
    animation: scaleHover 0.2s ease-out forwards;
  }
  
  .button-press:active {
    animation: scalePress 0.1s ease-out forwards;
  }

  .social-icon {
    transition: all 0.3s ease-out;
  }
  
  .social-icon:hover {
    transform: scale(1.2);
  }
  
  .mobile-menu-enter {
    opacity: 0;
    height: 0;
    transform: translateY(-10px);
  }
  
  .mobile-menu-enter-active {
    opacity: 1;
    height: auto;
    transform: translateY(0);
    transition: all 0.3s ease-out;
  }
  
  .mobile-menu-exit {
    opacity: 1;
    height: auto;
    transform: translateY(0);
  }
  
  .mobile-menu-exit-active {
    opacity: 0;
    height: 0;
    transform: translateY(-10px);
    transition: all 0.3s ease-out;
  }
`;

// Custom SVG Icons (memoized for performance)
const TwitterXIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const LinkedInIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const InstagramIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

const DiscordIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0190 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9460 2.4189-2.1568 2.4189Z" />
  </svg>
);

// Debounced scroll handler for better performance
const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);

  return useCallback(
    (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => callback(...args), delay);
    },
    [callback, delay]
  );
};

export function LandingTopbar({ className }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, isAuthenticated, logout, user } = useAuthStore();
  const [scrolled, setScrolled] = useState(false);
  const { theme, setTheme } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Cycle through themes: light → dark → system → light
  const cycleTheme = useCallback(() => {
    const themeOrder = ["light", "dark", "system"];
    const currentIndex = themeOrder.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themeOrder.length;
    setTheme(themeOrder[nextIndex]);
  }, [theme, setTheme]);
  const [activeItem, setActiveItem] = useState(null);
  const [isNavDropdownOpen, setIsNavDropdownOpen] = useState(false);

  // Optimized scroll handling with debouncing
  const ref = useRef(null);
  const { scrollY } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const [visible, setVisible] = useState(false);

  // Debounced scroll handler to reduce re-renders
  const debouncedScrollHandler = useDebounce((latest) => {
    const shouldBeVisible = latest > 100;
    if (shouldBeVisible !== visible) {
      setVisible(shouldBeVisible);
      setScrolled(shouldBeVisible);
    }
  }, 16); // ~60fps

  useMotionValueEvent(scrollY, "change", debouncedScrollHandler);

  // Inject optimized animations
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = topbarAnimations;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Memoized handlers to prevent unnecessary re-renders
  const handleMenuItemClick = useCallback(
    (path) => {
      if (path) {
        // Handle pricing scroll on landing page
        if (
          path === "#pricing" &&
          (location.pathname === "/" || location.pathname === "")
        ) {
          const pricingSection = document.getElementById("pricing");
          if (pricingSection) {
            pricingSection.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
          setActiveItem(null);
          return;
        }

        // Handle pricing navigation from other pages
        if (path === "/#pricing" && location.pathname !== "/") {
          navigate("/");
          // Small delay to ensure navigation completes before scrolling
          setTimeout(() => {
            const pricingSection = document.getElementById("pricing");
            if (pricingSection) {
              pricingSection.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
          setActiveItem(null);
          return;
        }

        // Handle external paths differently
        if (path.startsWith("http")) {
          window.open(path, "_blank");
        } else {
          navigate(path);
        }

        // Reset active menu item
        setActiveItem(null);
      }
    },
    [location.pathname, navigate]
  );

  // Memoized logout handler
  const handleLogout = useCallback(async () => {
    try {
      // Save theme preference before logout
      const currentTheme = localStorage.getItem("theme");

      // Call auth store logout which handles state cleanup
      const { success, error } = await logout();

      if (success) {
        // Clear localStorage but preserve theme setting
        const keysToKeep = ["theme"];
        const savedValues = {};

        // Save values we want to keep
        keysToKeep.forEach((key) => {
          savedValues[key] = localStorage.getItem(key);
        });

        // Clear localStorage
        localStorage.clear();

        // Restore values we want to keep
        Object.entries(savedValues).forEach(([key, value]) => {
          if (value) localStorage.setItem(key, value);
        });

        // Clear any session cookies
        document.cookie.split(";").forEach((c) => {
          document.cookie = c
            .replace(/^ +/, "")
            .replace(
              /=.*/,
              "=;expires=" + new Date().toUTCString() + ";path=/"
            );
        });

        // Navigate to home page using React Router
        navigate("/", { replace: true });
      } else {
        console.error("Logout failed:", error);
        // Clear the intentional logout flag if logout failed
        localStorage.removeItem("intentional_logout");
      }
    } catch (err) {
      console.error("Error during logout:", err);
      // Clear the intentional logout flag if there was an error
      localStorage.removeItem("intentional_logout");
    }
  }, [logout, navigate]);

  // Memoized navigation handler
  const handleNavigation = useCallback(
    (e, path) => {
      // Handle pricing scroll on landing page
      if (
        path === "#pricing" &&
        (location.pathname === "/" || location.pathname === "")
      ) {
        e.preventDefault();
        const pricingSection = document.getElementById("pricing");
        if (pricingSection) {
          pricingSection.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
        return;
      }

      // Handle pricing navigation from other pages
      if (path === "/#pricing" && location.pathname !== "/") {
        e.preventDefault();
        navigate("/#pricing");
        // Small delay to ensure navigation completes before scrolling
        setTimeout(() => {
          const pricingSection = document.getElementById("pricing");
          if (pricingSection) {
            pricingSection.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
        return;
      }

      // If it's an external link, handle it specially
      if (path.startsWith("http")) {
        e.preventDefault();
        window.open(path, "_blank");
        return;
      }

      // Close nav dropdown if it's open
      if (isNavDropdownOpen) {
        setIsNavDropdownOpen(false);
      }

      // Reset the active menu item
      setActiveItem(null);
    },
    [location.pathname, navigate, isNavDropdownOpen]
  );

  // Memoized navigation items
  const navItems = useMemo(
    () => [
      { name: "Features", link: "/features" },
      { name: "Changelog", link: "/changelog" },
      { name: "Documentation", link: "/docs" },
      { name: "News", link: "/news" },
      { name: "Support", link: "/support" },
      { name: "Pricing", link: "/pricing" },
    ],
    []
  );

  // Memoized social media items
  const socialItems = useMemo(
    () => [
      { Icon: TwitterXIcon, href: "https://x.com/artifact_chat" },
      {
        Icon: LinkedInIcon,
        href: "https://www.linkedin.com/company/artifact-chat/",
      },
      { Icon: InstagramIcon, href: "https://www.instagram.com/artifact.chat" },
      { Icon: DiscordIcon, href: "https://discord.com/invite/rBqn5m2p" },
    ],
    []
  );

  // Custom HoveredLink component that uses React Router navigation
  const CustomHoveredLink = useCallback(
    ({ children, href, ...rest }) => {
      return (
        <HoveredLink
          href={href}
          onClick={(e) => {
            e.preventDefault();
            handleMenuItemClick(href);
          }}
          {...rest}
        >
          {children}
        </HoveredLink>
      );
    },
    [handleMenuItemClick]
  );

  // Memoized display name
  const displayName = useMemo(
    () => profile?.full_name || profile?.email || "User",
    [profile?.full_name, profile?.email]
  );

  // Memoized avatar initial
  const getAvatarInitial = useCallback(() => {
    if (profile?.full_name && profile?.full_name.length > 0) {
      return profile.full_name[0];
    }
    if (profile?.email && profile?.email.length > 0) {
      return profile?.email[0];
    }
    return "U";
  }, [profile?.full_name, profile?.email]);

  // Optimized style objects
  const desktopNavStyle = useMemo(
    () => ({
      backdropFilter: scrolled ? "blur(10px)" : "none",
      boxShadow: scrolled
        ? "0 0 24px rgba(34, 42, 53, 0.06), 0 1px 1px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(34, 42, 53, 0.04), 0 0 4px rgba(34, 42, 53, 0.08), 0 16px 68px rgba(47, 48, 55, 0.05), 0 1px 0 rgba(255, 255, 255, 0.1) inset"
        : "none",
      width: visible ? "60%" : "100%",
      transform: `translateY(${visible ? 20 : 0}px)`,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    }),
    [scrolled, visible]
  );

  const mobileNavStyle = useMemo(
    () => ({
      backdropFilter: scrolled ? "blur(10px)" : "none",
      boxShadow: scrolled
        ? "0 0 24px rgba(34, 42, 53, 0.06), 0 1px 1px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(34, 42, 53, 0.04), 0 0 4px rgba(34, 42, 53, 0.08), 0 16px 68px rgba(47, 48, 55, 0.05), 0 1px 0 rgba(255, 255, 255, 0.1) inset"
        : "none",
      width: visible ? "90%" : "100%",
      paddingRight: visible ? "12px" : "16px",
      paddingLeft: visible ? "12px" : "16px",
      borderRadius: visible ? "4px" : "0",
      transform: `translateY(${visible ? 20 : 0}px)`,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    }),
    [scrolled, visible]
  );

  return (
    <div ref={ref} className={cn("sticky top-0 z-50 w-full", className)}>
      {/* Desktop Navigation */}
      <div
        style={desktopNavStyle}
        className={cn(
          "relative z-[60] mx-auto hidden w-full max-w-7xl items-center justify-between self-start rounded-full px-4 py-2 lg:flex",
          scrolled &&
            "bg-background/80 backdrop-blur-md border-b border-purple-500/20 dark:border-purple-500/20 border-purple-300/30",
          className
        )}
      >
        {/* Desktop Layout */}
        <div className="flex items-center justify-between w-full">
          {/* Left side with logo */}
          <div className="flex items-center">
            {/* Logo */}
            <div className="relative z-20 flex items-center mr-6">
              <Link to="/" className="flex items-center space-x-3">
                <div className="relative">
                  <ArtifactLogo size="xs" animate={true} />
                  <div className="absolute inset-0 -z-10 bg-purple-500/20 dark:bg-purple-500/20 bg-purple-400/30 blur-xl rounded-full transform scale-75 opacity-70" />
                </div>
              </Link>
            </div>

            {/* Navigation Items - Simple links when not scrolled */}
            {!visible && (
              <div className="flex items-center space-x-6">
                {navItems.map((item, idx) =>
                  item.isExternal ? (
                    <a
                      key={`standard-nav-${idx}`}
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        "text-sm font-medium relative px-2 py-1 transition-colors duration-200",
                        "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {item.name}
                    </a>
                  ) : item.link.startsWith("#") ||
                    item.link.startsWith("/#") ? (
                    <button
                      key={`standard-nav-${idx}`}
                      onClick={(e) => handleNavigation(e, item.link)}
                      className={cn(
                        "text-sm font-medium relative px-2 py-1 transition-colors duration-200 bg-transparent border-none cursor-pointer",
                        "text-muted-foreground hover:text-foreground hover:bg-transparent focus:outline-none focus:bg-transparent active:bg-transparent"
                      )}
                    >
                      {item.name}
                    </button>
                  ) : (
                    <Link
                      key={`standard-nav-${idx}`}
                      to={item.link}
                      className={cn(
                        "text-sm font-medium relative px-2 py-1 transition-colors duration-200",
                        location.pathname === item.link ||
                          (item.link === "/docs" &&
                            location.pathname.startsWith("/docs"))
                          ? "text-purple-600 dark:text-purple-300"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                      onClick={(e) => handleNavigation(e, item.link)}
                    >
                      {item.name}
                    </Link>
                  )
                )}
              </div>
            )}

            {/* Navbar Menu - Only show when scrolled */}
            {visible && (
              <div className="flex items-center relative z-30">
                <Menu setActive={setActiveItem} className="py-0">
                  <MenuItem
                    key="main-menu"
                    setActive={setActiveItem}
                    active={activeItem}
                    item="Menu"
                    className="bg-transparent px-3 py-1 text-sm font-medium"
                  >
                    <div className="grid grid-cols-1 gap-0 p-0 w-[200px]">
                      {navItems.map((item, idx) => (
                        <CustomHoveredLink
                          key={`menu-item-${idx}`}
                          href={item.link}
                          className="py-2 px-3"
                        >
                          {item.name}
                        </CustomHoveredLink>
                      ))}
                    </div>
                  </MenuItem>
                </Menu>
              </div>
            )}
          </div>

          {/* Right side with social links and auth */}
          <div className="flex items-center space-x-4">
            {/* Social Media Icons */}
            <div className="hidden md:flex items-center space-x-4">
              {socialItems.map((social, index) => (
                <a
                  key={social.href}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-purple-400 dark:hover:text-purple-400 hover:text-purple-500 transition-colors duration-300 social-icon"
                >
                  <social.Icon className="w-4 h-4" />
                </a>
              ))}
            </div>

            {/* Theme Toggle Button - Cycles through light → dark → system */}
            <Button
              variant="ghost"
              size="icon"
              onClick={cycleTheme}
              className="hidden md:flex h-8 w-8 items-center justify-center rounded-full bg-background/50 border border-purple-500/20 dark:border-purple-500/20 border-purple-300/30 p-0 hover:bg-purple-500/10 dark:hover:bg-white/15"
              title={`Theme: ${theme} (click to cycle)`}
            >
              {theme === "light" && (
                <Sun className="h-3.5 w-3.5 text-yellow-500" />
              )}
              {theme === "dark" && (
                <Moon className="h-3.5 w-3.5 text-purple-300" />
              )}
              {theme === "system" && (
                <Laptop className="h-3.5 w-3.5 text-blue-400" />
              )}
              <span className="sr-only">Toggle theme</span>
            </Button>

            {/* Auth Button - Only show when user is not signed in */}
            {!user && (
              <Link to="/auth/login">
                <button className="px-5 py-1.5 rounded-full text-sm font-medium text-foreground dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-white dark:hover:text-gray-900 dark:hover:border-white transition-all duration-200">
                  Login
                </button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div
        style={mobileNavStyle}
        className={cn(
          "relative z-50 mx-auto flex lg:hidden w-full max-w-[calc(100vw-2rem)] flex-col items-center justify-between py-2",
          scrolled &&
            "bg-background/80 backdrop-blur-md border-b border-purple-500/20 dark:border-purple-500/20 border-purple-300/30",
          className
        )}
      >
        <div className="flex w-full flex-row items-center justify-between">
          {/* Mobile Logo */}
          <Link to="/" className="relative z-20 flex items-center space-x-3">
            <div className="relative">
              <ArtifactLogo size="sm" animate={true} />
              <div className="absolute inset-0 -z-10 bg-purple-500/20 dark:bg-purple-500/20 bg-purple-400/30 blur-xl rounded-full transform scale-75 opacity-70" />
            </div>
          </Link>

          {/* Mobile Menu Toggle */}
          <div className="flex items-center space-x-3">
            {/* Mobile Theme Toggle - Cycles through light → dark → system */}
            <Button
              variant="ghost"
              size="icon"
              onClick={cycleTheme}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-background/50 border border-purple-500/20 dark:border-purple-500/20 border-purple-300/30 p-0 hover:bg-purple-500/10 dark:hover:bg-white/15"
              title={`Theme: ${theme} (click to cycle)`}
            >
              {theme === "light" && (
                <Sun className="h-3.5 w-3.5 text-yellow-500" />
              )}
              {theme === "dark" && (
                <Moon className="h-3.5 w-3.5 text-purple-300" />
              )}
              {theme === "system" && (
                <Laptop className="h-3.5 w-3.5 text-blue-400" />
              )}
              <span className="sr-only">Toggle theme</span>
            </Button>

            {isMenuOpen ? (
              <X
                className="text-foreground h-5 w-5 cursor-pointer"
                onClick={() => setIsMenuOpen(false)}
              />
            ) : (
              <MenuIcon
                className="text-foreground h-5 w-5 cursor-pointer"
                onClick={() => setIsMenuOpen(true)}
              />
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div
            className={cn(
              "absolute inset-x-0 top-14 z-50 flex w-full flex-col items-start justify-start gap-4 rounded-lg bg-background/95 backdrop-blur-md px-4 py-4 shadow-[0_0_24px_rgba(34,_42,_53,_0.06),_0_1px_1px_rgba(0,_0,_0,_0.05),_0_0_0_1px_rgba(34,_42,_53,_0.04),_0_0_4px_rgba(34,_42,_53,_0.08),_0_16px_68px_rgba(47,_48,_55,_0.05),_0_1px_0_rgba(255,_255,_255,_0.1)_inset] border border-purple-500/20 dark:border-purple-500/20 border-purple-300/30",
              "topbar-fade-in"
            )}
          >
            {/* Mobile Navigation Items */}
            <div className="w-full flex flex-col space-y-3">
              {navItems.map((item, idx) =>
                item.isExternal ? (
                  <a
                    key={`mobile-nav-${idx}`}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      "px-3 py-2 w-full rounded-lg transition-colors duration-200",
                      "hover:bg-purple-500/10 text-foreground dark:text-foreground"
                    )}
                  >
                    {item.name}
                  </a>
                ) : item.link.startsWith("#") || item.link.startsWith("/#") ? (
                  <button
                    key={`mobile-nav-${idx}`}
                    onClick={(e) => {
                      handleNavigation(e, item.link);
                      setIsMenuOpen(false);
                    }}
                    className={cn(
                      "px-3 py-2 w-full rounded-lg transition-colors duration-200 text-left bg-transparent border-none cursor-pointer",
                      "hover:bg-purple-500/10 text-foreground dark:text-foreground focus:outline-none focus:bg-transparent active:bg-transparent"
                    )}
                  >
                    {item.name}
                  </button>
                ) : (
                  <Link
                    key={`mobile-nav-${idx}`}
                    to={item.link}
                    className={cn(
                      "px-3 py-2 w-full rounded-lg transition-colors duration-200",
                      "hover:bg-purple-500/10 text-foreground dark:text-foreground",
                      location.pathname === item.link ||
                        (item.link === "/docs" &&
                          location.pathname.startsWith("/docs"))
                        ? "bg-purple-500/10 text-purple-600 dark:text-purple-300"
                        : ""
                    )}
                    onClick={(e) => {
                      handleNavigation(e, item.link);
                      setIsMenuOpen(false);
                    }}
                  >
                    {item.name}
                  </Link>
                )
              )}
            </div>

            {/* Mobile Social Media Icons */}
            <div className="flex items-center space-x-5 w-full pt-2 border-t border-purple-500/20 dark:border-purple-500/20 border-purple-300/30">
              {socialItems.map((social, index) => (
                <a
                  key={`mobile-social-${index}`}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-purple-400 dark:hover:text-purple-400 hover:text-purple-500 transition-colors duration-300 p-1.5 social-icon"
                >
                  <social.Icon className="w-4 h-4" />
                </a>
              ))}
            </div>

            {/* Mobile Auth Button - Only show when user is not signed in */}
            {!user && (
              <div className="flex w-full pt-2 border-t border-purple-500/20 dark:border-purple-500/20 border-purple-300/30">
                <Link
                  to="/auth/login"
                  className="w-full"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <button className="w-full px-4 py-1.5 rounded-full text-sm font-medium text-foreground dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-white dark:hover:text-gray-900 dark:hover:border-white transition-all duration-200">
                    Login
                  </button>
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Animated gradient border at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-purple-500/30 to-transparent dark:via-purple-500/30 via-purple-300/40 opacity-100 transition-opacity duration-300" />
    </div>
  );
}

export default LandingTopbar;
